import { Box, Card, Typography } from '@mui/material';
import i18n from 'i18next';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IOSSwitch } from '../../../shared/components/IOSSwitch';
import { ELanguage } from '../../../shared/enums/user/ELanguage';
import { ERole } from '../../../shared/enums/user/ERole';
import { ETheme } from '../../../shared/enums/user/ETheme';
import { handleError } from '../../../shared/functions/handleError';
import { IUser } from '../../../shared/interfaces/user/IUser';
import { darkPalette, lightPalette } from '../../../shared/mui/palettes';
import authSlice from '../../../store/slices/auth.slice';
import companySettingsSlice from '../../../store/slices/company-settings.slice';
import { RootState } from '../../../store/store';
import { UserEmployeeService } from '../../user/employees/service';
import { AdminEmployeeService } from '../employees/service';
import CompanyInformation from './components/CompanyInformation';
import SettingsDropdown from './components/SettingsDropdown';

const keys = [
  {
    name: 'appearance',
    roles: [ERole.DEVELOPER, ERole.ADMIN, ERole.USER],
  },
  { name: 'language', roles: [ERole.DEVELOPER, ERole.ADMIN, ERole.USER] },
  { name: 'email', roles: [ERole.DEVELOPER, ERole.ADMIN, ERole.USER] },
  {
    name: 'pushNotifications',
    roles: [ERole.DEVELOPER, ERole.ADMIN, ERole.USER],
  },
];

const AdminSettings = () => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state?.auth);
  const company = useSelector((state: RootState) => state?.companySettings);
  const {
    theme,
    language,
    isEmailNotificationEnabled,
    isPushNotificationEnabled,
  } = userData as IUser;
  const dispatch = useDispatch();

  const handleUpdateSettings = async (
    userKey: string,
    value: string | boolean,
  ) => {
    const formData = new FormData();
    const correctService =
      userData?.role === ERole?.USER
        ? UserEmployeeService
        : AdminEmployeeService;
    formData.append('body', JSON.stringify({ [userKey]: value }));

    !!userData?.id &&
      (await correctService
        .update(formData, userData?.id)
        .then((response) => {
          if (response) {
            enqueueSnackbar(
              t('toasts.successfullyUpdated', { title: userData?.displayName }),
              {
                variant: 'success',
              },
            );
            i18n.changeLanguage(response?.language);
            dispatch(
              authSlice.actions.setLoginState({
                userData: {
                  ...userData,
                  [userKey]: value,
                },
                isAuthenticated: true,
              }),
            );
            dispatch(companySettingsSlice.actions.setCompanySettings(company));
          }
        })
        .catch((err) => handleError(err)));
  };
  const settingsComponents = {
    appearance: (
      <SettingsDropdown
        items={Object.values(ETheme)?.map((item) => ({
          key: item,
          label: `theme.${item}`,
        }))}
        label={'settings.appearance'}
        onChange={(value) => handleUpdateSettings('theme', value)}
        value={theme}
      />
    ),
    language: (
      <SettingsDropdown
        items={Object.values(ELanguage)?.map((item) => ({
          key: item,
          label: `language.${item}`,
        }))}
        label={'settings.language'}
        onChange={(value) => handleUpdateSettings('language', value)}
        value={language}
      />
    ),
    email: (
      <IOSSwitch
        checked={isEmailNotificationEnabled}
        onChange={(e) =>
          handleUpdateSettings('isEmailNotificationEnabled', e.target.checked)
        }
      />
    ),
    pushNotifications: (
      <IOSSwitch
        checked={isPushNotificationEnabled}
        onChange={(e) =>
          handleUpdateSettings('isPushNotificationEnabled', e.target.checked)
        }
      />
    ),
  };
  return (
    <>
      <Card sx={{ mb: '20px' }}>
        {keys
          ?.filter((setting) =>
            setting?.roles?.includes(userData?.role as ERole),
          )
          ?.map((setting, index) => {
            const isLastIndex = keys?.length - 1 === index;
            return (
              <Box
                key={setting?.name}
                sx={{
                  paddingTop: '20px',
                  paddingBottom: '20px',
                  borderBottom: isLastIndex
                    ? 'none'
                    : `1px solid ${theme === ETheme.LIGHT ? lightPalette.primary.light : darkPalette.background.light}`,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography>{t(`settings.${setting?.name}`)}</Typography>
                    <Typography
                      variant={'subtitle2'}
                      color={lightPalette.text.gray}
                      sx={{
                        fontWeight: '300',
                      }}
                    >
                      {t(`settings.${setting?.name}_desc`)}
                    </Typography>
                  </Box>
                  <div>
                    {
                      settingsComponents?.[
                        setting?.name as keyof typeof settingsComponents
                      ]
                    }
                  </div>
                </Box>
              </Box>
            );
          })}
      </Card>
      {userData?.role !== ERole?.USER && <CompanyInformation />}
    </>
  );
};

export default AdminSettings;
